import apiService from './apiService';

const providerFormService = {
  /**
   * Szolgáltató formjainak lekérdezése.
   * @param {number} providerId - A szolgáltató ID-ja.
   * @returns {Promise<object>}
   */
  getProviderForms: async (providerId) => {
    try {
      const response = await apiService.get(`/provider-forms/provider/${providerId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching forms for provider ${providerId}:`, error);
      throw error;
    }
  },

  /**
   * Egy adott form lekérdezése ID alapján.
   * @param {number} formId - A form ID-ja.
   * @returns {Promise<object>}
   */
  getProviderFormById: async (formId) => {
    try {
      const response = await apiService.get(`/provider-forms/${formId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Új szolgáltató form létrehozása.
   * @param {object} formData - A form adatai (title, description, isActive).
   * @returns {Promise<object>}
   */
  createProviderForm: async (formData) => {
    try {
      const response = await apiService.post('/provider-forms', formData);
      return response;
    } catch (error) {
      console.error('Error creating provider form:', error);
      throw error;
    }
  },

  /**
   * Szolgáltató form frissítése.
   * @param {number} formId - A form ID-ja.
   * @param {object} formData - A frissítendő adatok.
   * @returns {Promise<object>}
   */
  updateProviderForm: async (formId, formData) => {
    try {
      const response = await apiService.put(`/provider-forms/${formId}`, formData);
      return response;
    } catch (error) {
      console.error(`Error updating form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Szolgáltató form törlése.
   * @param {number} formId - A form ID-ja.
   * @returns {Promise<object>}
   */
  deleteProviderForm: async (formId) => {
    try {
      const response = await apiService.delete(`/provider-forms/${formId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Új mező hozzáadása egy formhoz.
   * @param {number} formId - A form ID-ja.
   * @param {object} fieldData - A mező adatai.
   * @returns {Promise<object>}
   */
  addFormField: async (formId, fieldData) => {
    try {
      const response = await apiService.post(`/provider-forms/${formId}/fields`, fieldData);
      return response;
    } catch (error) {
      console.error(`Error adding field to form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Mező frissítése egy formon belül.
   * @param {number} formId - A form ID-ja.
   * @param {number} fieldId - A mező ID-ja.
   * @param {object} fieldData - A frissítendő adatok.
   * @returns {Promise<object>}
   */
  updateFormField: async (formId, fieldId, fieldData) => {
    try {
      const response = await apiService.put(`/provider-forms/${formId}/fields/${fieldId}`, fieldData);
      return response;
    } catch (error) {
      console.error(`Error updating field ${fieldId} in form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Mezők sorrendjének mentése egyetlen kérésben.
   * @param {number} formId - A form ID-ja.
   * @param {number[]} fieldIds - A form összes mezőjének ID-ja az új sorrendben.
   * @returns {Promise<object>}
   */
  reorderFormFields: async (formId, fieldIds) => {
    try {
      const response = await apiService.put(`/provider-forms/${formId}/fields/reorder`, { fieldIds });
      return response;
    } catch (error) {
      console.error(`Error reordering fields in form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Mező törlése egy formon belül.
   * @param {number} formId - A form ID-ja.
   * @param {number} fieldId - A mező ID-ja.
   * @returns {Promise<object>}
   */
  deleteFormField: async (formId, fieldId) => {
    try {
      const response = await apiService.delete(`/provider-forms/${formId}/fields/${fieldId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting field ${fieldId} from form ${formId}:`, error);
      throw error;
    }
  },

  /**
   * Egy szolgáltató aktív formjának lekérdezése (nyilvános).
   * @param {number} providerId - A szolgáltató ID-ja.
   * @returns {Promise<object>}
   */
  getActiveProviderFormForPublic: async (providerId) => {
    try {
      const response = await apiService.get(`/provider-forms/public/${providerId}/active`);
      return response;
    } catch (error) {
      console.error(`Error fetching active form for provider ${providerId}:`, error);
      throw error;
    }
  },

  /**
   * Form válasz beküldése.
   * @param {number} formId - A form ID-ja.
   * @param {number} bookingId - A foglalás ID-ja, amihez a válasz tartozik.
   * @param {Array<object>} fieldResponses - A mezőválaszok tömbje (pl. [{ fieldId: 1, value: "válasz" }]).
   * @returns {Promise<object>}
   */
  submitFormResponse: async (formId, bookingId, fieldResponses) => {
    try {
      const response = await apiService.post(`/provider-forms/${formId}/submit-response`, {
        bookingId,
        fieldResponses,
      });
      return response;
    } catch (error) {
      console.error(`Error submitting form response for form ${formId}:`, error);
      throw error;
    }
  },
  /**
   * Egy adott FormResponse lekérdezése ID alapján.
   * @param {number} responseId - A FormResponse ID-ja.
   * @returns {Promise<object>}
   */
  getFormResponseById: async (responseId) => {
    try {
      const response = await apiService.get(`/provider-forms/responses/${responseId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching form response ${responseId}:`, error);
      throw error;
    }
  },

  /**
   * Lekéri vagy létrehozza a provider alapértelmezett formját.
   * @param {number} providerId - A szolgáltató ID-ja.
   * @returns {Promise<object>}
   */
  ensureDefaultForm: async (providerId) => {
    try {
      const response = await apiService.get(`/provider-forms/provider/${providerId}/ensure-default`);
      return response;
    } catch (error) {
      console.error(`Error ensuring default form for provider ${providerId}:`, error);
      throw error;
    }
  },

  /**
   * Egy booking-hoz tartozó form válasz lekérdezése.
   * @param {number} bookingId - A foglalás ID-ja.
   * @returns {Promise<object>}
   */
  getFormResponseByBookingId: async (bookingId) => {
    try {
      const response = await apiService.get(`/provider-forms/booking/${bookingId}/response`);
      return response;
    } catch (error) {
      console.error(`Error fetching form response for booking ${bookingId}:`, error);
      throw error;
    }
  },

  /**
   * Form-alapú foglalás létrehozása.
   * @param {object} data - { providerId, formId, fieldResponses, weddingEventId }
   * @returns {Promise<object>}
   */
  createBookingWithForm: async (data) => {
    try {
      const response = await apiService.post('/bookings/with-form', data);
      return response;
    } catch (error) {
      console.error('Error creating booking with form:', error);
      throw error;
    }
  },
};

export default providerFormService;